/** @file Editor for fraction inputs in the app. */
import Big from 'big.js';
import { Left, Right } from 'purify-ts';
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { modifiers as m } from 'vue-tsx-support';
import { Fraction } from '@codegrade/bigfraction';
import { Just, Maybe } from '@codegrade/maybe';
import { humanize } from '@codegrade/utils';
import { valueProp } from './utils';
export default tsx.componentFactoryOf().create({
    name: 'fraction-editor',
    props: {
        name: p(String).default('Value'),
        value: valueProp().required,
        disabled: p(Boolean).default(false),
        placeholder: p(String).optional,
        min: p(Fraction).optional,
        max: p(Fraction).optional,
        /** The extra classes to give to the input. */
        inputClass: p.ofType().default(''),
        step: p(Fraction).default(() => new Fraction(1)),
        /**
         * Force a specific amount of decimals to be emitted, even when the input fraction
         * does not have a finite decimal representation in base 10.
         */
        fixedDecimals: p(Number).optional,
        /**
         * When set, try to render the exact value of the fraction with this amount of
         * decimal places. If the value does not fit it is rendered as a fraction.
         */
        maxDecimals: p(Number).optional,
        /** The tabindex for the input. */
        tabindex: p(Number).default(0),
        /** The id of the label element for the input. */
        labelledBy: p(String).optional,
    },
    /** @returns Object storing the user input as an unparsed string. */
    data() {
        return {
            userInput: '',
        };
    },
    watch: {
        value: {
            immediate: true,
            /** Attempts to parse the input. If it is not possible, an error is returned. */
            handler() {
                this.value.ifRight(maybeValue => maybeValue.caseOf({
                    Just: value => {
                        this.decodeValue(this.userInput)
                            .chain(existing => {
                            if (existing.equals(value)) {
                                return Right(value);
                            }
                            return Left('Change value');
                        })
                            .ifLeft(() => {
                            this.userInput = this.formatValue(value);
                        });
                    },
                    Nothing: () => {
                        this.userInput = '';
                    },
                }));
            },
        },
    },
    methods: {
        /**
         * Decodes a string that looks like a decimal into a fraction.
         *
         * @param strValue The string value.
         * @returns Either a fraction or the error.
         */
        decodeValue(strValue) {
            try {
                // fraction.js interprets '-' as 0...
                if (strValue === '-') {
                    throw new Error();
                }
                return Right(new Fraction(strValue));
            }
            catch {
                return Left(new Error(`Expected a fractional number, but got the string "${strValue}"`));
            }
        },
        /**
         * Sets the correct string value and emits the partial input obtained so far.
         *
         * Emits `Right(Nothing)` if the input is empty, the decoded value if acceptable,
         * or an error otherwise.
         *
         * @param strValue The string value from the input.
         */
        onInput(strValue) {
            const toEmit = Maybe.fromPredicate(x => x !== '', strValue).caseOf({
                Just: value => this.decodeValue(value).chain(this.checkValue),
                Nothing: () => Right(Maybe.empty()),
            });
            tsx.emitOn(this, 'onInput', toEmit);
            this.userInput = strValue;
        },
        /**
         * Validates whether the fraction fits the constraints.
         *
         * Checks agains the `min` and `max` values of the components if set.
         *
         * @param value The fraction to check.
         * @returns Either the description of the issue or the Maybe of the fraction.
         */
        checkValue(value) {
            if (this.min != null && value.compare(this.min) < 0) {
                const min = humanize.fraction(this.min);
                return Left(Error(`${this.name} should be greater than or equal to ${min}.`));
            }
            if (this.max != null && value.compare(this.max) > 0) {
                const max = humanize.fraction(this.max);
                return Left(Error(`${this.name} should be less than or equal to ${max}.`));
            }
            return Right(new Just(value));
        },
        /**
         * Formats the fraction to a human readable format.
         *
         * @param value The fraction to format.
         * @returns The string representation of the fraction.
         */
        formatValue(value) {
            if (this.fixedDecimals != null) {
                // We wrap this in a `Big` to replicate the same behaviour as in
                // `models.Assignment.formatUnscaledGrade` to have consistent grades
                // Throughout the app.
                // TODO: This should use `humanize.fraction` with SCRUM-588
                return new Big(value.valueOf()).toFixed(this.fixedDecimals);
            }
            return humanize.fraction(value, this.maxDecimals);
        },
        /** Reformats the userInput if there is a valid value. */
        reformatValue() {
            this.value.ifRight(maybeValue => {
                maybeValue.ifJust(value => {
                    this.userInput = this.formatValue(value);
                });
            });
        },
        /** Increase the value using the step specified in the component. */
        incValue() {
            this.maybeUpdate(v => v.add(this.step));
        },
        /** Decrease the value using the step specified in the component. */
        decValue() {
            this.maybeUpdate(v => v.sub(this.step));
        },
        /**
         * Updates the current value with the provided updater.
         *
         * Is applied only if the current value in the input is parsable.
         *
         * @param f The updater funtion.
         */
        maybeUpdate(f) {
            this.decodeValue(this.userInput).ifRight(value => {
                let ret = f(value);
                if (this.max != null && ret.compare(this.max) > 0) {
                    ret = this.max;
                }
                else if (this.min != null && ret.compare(this.min) < 0) {
                    ret = this.min;
                }
                this.onInput(ret.toFraction());
            });
        },
    },
    /** @returns The rendered component. */
    render() {
        const handleUp = m.up.prevent(this.incValue);
        const handleDown = m.down.prevent(this.decValue);
        const label = this.labelledBy != null ? undefined : `Fraction input for ${this.name}`;
        return (<input name={this.name} disabled={this.disabled} placeholder={this.placeholder} class={['form-control text-right', this.inputClass]} value={this.userInput} onInput={payload => this.onInput(payload.target.value)} onKeydown={event => {
                tsx.emitOn(this, 'onKeydown', event);
                if (!event.defaultPrevented) {
                    handleUp(event);
                    handleDown(event);
                }
            }} aria-label={label} aria-labelledby={this.labelledBy} tabindex={this.tabindex}/>);
    },
});
